import React from "react"
import { observer } from "mobx-react-lite"

import Button from "@components/ui/Button/Button"
import Icon from "@components/ui/Icon/Icon"
import Text from "@components/ui/Typography/Text"
import { useAppConfig } from "@components/theme/AppConfigProvider"
import { useStore } from "@store/index"
import chatService from "@services/chat.service"
import userService from "@services/user.service"
import {
  copilotStudioClientId,
  copilotStudioDirectConnectUrl,
  copilotStudioEnvironmentId,
  copilotStudioSchemaName,
} from "@framework/constants/copilot-studio"

import {
  beginCopilotStudioSignIn,
  clearCopilotStudioToken,
  completeCopilotStudioSignIn,
  copilotSilentSignInNeedsInteraction,
  copilotStudioAuthMode,
  readCopilotLoginHint,
  readCopilotStudioToken,
  refreshCopilotStudioToken,
  rememberCopilotLoginHint,
} from "./copilotStudioAuth"
import {
  agentRepliesFromActivities,
  CopilotCitation,
  sendCopilotMessage,
  startCopilotConversation,
} from "./copilotStudioClient"

import CopilotMessageBody from "./formatCopilotMessage"
import CopilotCitationPanel, { CitationPanelSource } from "./CopilotCitationPanel"
import {
  citationLabel,
  citationsToSave,
  citeTableRows,
  claimsForMarker,
  markersNeedingPassages,
  positionedCitations,
  readableSourceLinks,
  sameDocumentUrl,
  withPassages,
} from "./copilotCitations"
import {
  BRAND_ACCESS_DENIED,
  citationIsAllowed,
  CopilotBrandScope,
  copilotBrandActivityValue,
  restrictCopilotReply,
  scopeCopilotQuestion,
} from "./copilotBrandScope"
import {
  alignSharePointCitations,
  citationsFromText,
  citesSharePointDocument,
  isCopilotCapacityMessage,
  mergeCitations,
  reviewSharePointSources,
  reviewBesideProductPhotos,
  ReviewedSource,
  SHAREPOINT_RETRIEVAL_SUCCESS,
} from "./copilotSourceQuality"
import {
  attachmentContentType,
  isImageAttachment,
  presentImageAnswer,
  reviewImageAnswer,
} from "./copilotImageQuality"
import {
  isPdfAttachment,
  pdfNameFromText,
  reviewPdfAnswer,
} from "./copilotPdfQuality"
import {
  reviewChartAnswer,
  withChartLookupGuidance,
  withChartReadingGuidance,
} from "./copilotChartQuality"
import {
  attachmentMetaFromFile,
  attachmentsFromText,
  CopilotAttachmentMeta,
  firstImageAttachment,
  userMessageDisplayText,
} from "./copilotAttachments"
import CopilotAttachmentCard from "./CopilotAttachmentCard"
import {
  COMPARISON_FOLLOW_UP,
  needsComparisonFollowUp,
} from "./copilotAnswerGuides"
import {
  CopilotProductImageResult,
  isProductImageRequest,
  splitProductImages,
  withProductImages,
} from "./copilotProductImages"
import {
  clearLastCopilotChatId,
  readLastCopilotChatId,
  rememberLastCopilotChatId,
} from "./copilotLanding"

import styles from "./CopilotStudioSdkChat.module.sass"

type ChatLine = {
  id: string
  role: "user" | "agent"
  text: string
  citations?: CopilotCitation[]
  imageUrl?: string
  imageName?: string
  pdfName?: string
  pdfExtraction?: string
  documentName?: string
  documentText?: string
  attachments?: CopilotAttachmentMeta[]
}

type SavedChat = {
  id: string
  title: string
  updatedAt: string
  pinned?: boolean
}

const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024
const PROMPT_INPUT_MAX_HEIGHT_PX = 220
const CHAT_TITLE_MAX_LENGTH = 60
/** The answer is already on screen; passages that take longer are looked up when a citation is opened. */
const CITATION_PASSAGE_TIMEOUT_MS = 15000

function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ""))
    reader.onerror = () => reject(new Error("Could not read the file"))
    reader.readAsDataURL(file)
  })
}

const CopilotStudioSdkChat: React.FC = observer(() => {
  const theme = useAppConfig()
  const { userStore } = useStore()
  const clientId =
    copilotStudioClientId || theme.services.microsoft?.clientId || ""
  const loginHint = userStore.user?.email || readCopilotLoginHint()
  const loginHintRef = React.useRef(loginHint)
  loginHintRef.current = loginHint

  const [token, setToken] = React.useState<string | null>(() =>
    readCopilotStudioToken()
  )
  const [conversationId, setConversationId] = React.useState("")
  const [chatId, setChatId] = React.useState("")
  const [chats, setChats] = React.useState<SavedChat[]>([])
  const [session, setSession] = React.useState(0)
  const [lines, setLines] = React.useState<ChatLine[]>([])
  const [draft, setDraft] = React.useState("")
  const [file, setFile] = React.useState<File | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [status, setStatus] = React.useState("")
  const [error, setError] = React.useState("")
  const [needsManualSignIn, setNeedsManualSignIn] = React.useState(false)
  const [brandScope, setBrandScope] = React.useState<CopilotBrandScope | null>(null)
  const [scopeReady, setScopeReady] = React.useState(false)
  const [menuChatId, setMenuChatId] = React.useState<string | null>(null)
  const [renamingId, setRenamingId] = React.useState<string | null>(null)
  const [renameDraft, setRenameDraft] = React.useState("")
  const [renameBusy, setRenameBusy] = React.useState(false)
  const [deletingChat, setDeletingChat] = React.useState<{
    id: string
    title: string
  } | null>(null)
  const [deleteBusy, setDeleteBusy] = React.useState(false)
  const [openCitation, setOpenCitation] = React.useState<{ lineId: string; position: number } | null>(null)
  const [passageLoadingId, setPassageLoadingId] = React.useState<string | null>(null)
  const passageLookups = React.useRef(new Set<string>())
  const brandScopeRef = React.useRef<CopilotBrandScope | null>(null)
  brandScopeRef.current = brandScope
  const chatIdRef = React.useRef("")
  const started = React.useRef(false)
  const authStarted = React.useRef(false)
  const listRef = React.useRef<HTMLDivElement>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const promptInputRef = React.useRef<HTMLTextAreaElement>(null)
  const historyMenuRef = React.useRef<HTMLDivElement>(null)

  const hasUserQuery = lines.some((line) => line.role === "user")

  const resizePromptInput = React.useCallback(() => {
    const el = promptInputRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, PROMPT_INPUT_MAX_HEIGHT_PX)}px`
  }, [])

  React.useLayoutEffect(() => {
    resizePromptInput()
  }, [draft, resizePromptInput])

  const connection = React.useMemo(
    () => ({
      environmentId: copilotStudioEnvironmentId,
      schemaName: copilotStudioSchemaName,
      directConnectUrl: copilotStudioDirectConnectUrl,
      token: token || "",
    }),
    [token]
  )

  const visibleReplies = (
    activities: Parameters<typeof agentRepliesFromActivities>[0]
  ): ChatLine[] =>
    agentRepliesFromActivities(activities)
      .map((reply) => ({
        id: `${Date.now()}-${Math.random()}`,
        role: "agent" as const,
        text: reply.text,
        citations: reply.citations,
      }))
      .map((reply) => {
        const aligned = alignSharePointCitations(
          reply.text,
          mergeCitations(reply.citations, citationsFromText(reply.text))
        )
        // LAB-113: keep one citation per [n] marker, not one per document.
        const citations = citationsToSave(aligned.text, [
          ...reply.citations,
          ...aligned.citations,
        ])
        return restrictCopilotReply(
          { ...reply, text: aligned.text, citations },
          brandScopeRef.current
        )
      })

  const refreshChats = async () => {
    const response = await userService.listCopilotChats()
    const next = (response.data.data || []).map((chat) => ({
      id: chat.id,
      title: chat.title,
      updatedAt: chat.updatedAt,
      pinned: Boolean(chat.pinned),
    }))
    // Most recently prompted first within each section.
    next.sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )
    setChats(next)
  }

  const pinnedChats = React.useMemo(
    () => chats.filter((chat) => chat.pinned),
    [chats]
  )
  const recentChats = React.useMemo(
    () => chats.filter((chat) => !chat.pinned),
    [chats]
  )

  React.useEffect(() => {
    if (!menuChatId) return undefined
    const onPointerDown = (event: MouseEvent) => {
      if (historyMenuRef.current?.contains(event.target as Node)) return
      setMenuChatId(null)
    }
    document.addEventListener("mousedown", onPointerDown)
    return () => document.removeEventListener("mousedown", onPointerDown)
  }, [menuChatId])

  const cancelRename = () => {
    setRenamingId(null)
    setRenameDraft("")
    setRenameBusy(false)
  }

  const leaveDeletedChat = () => {
    clearLastCopilotChatId()
    started.current = false
    chatIdRef.current = ""
    setChatId("")
    setConversationId("")
    setLines([])
    setDraft("")
    setFile(null)
    setError("")
    setMenuChatId(null)
    setDeletingChat(null)
    setDeleteBusy(false)
    cancelRename()
  }

  const cancelDeleteChat = () => {
    if (deleteBusy) return
    setDeletingChat(null)
    setDeleteBusy(false)
  }

  const beginDeleteChat = (chat: SavedChat) => {
    setMenuChatId(null)
    setDeletingChat({ id: chat.id, title: chat.title || "New chat" })
  }

  const onDeleteChat = async (id: string) => {
    setMenuChatId(null)
    setDeleteBusy(true)
    try {
      await userService.deleteCopilotChat(id)
      setChats((current) => current.filter((chat) => chat.id !== id))
      setDeletingChat(null)
      if (chatIdRef.current === id || chatId === id) {
        leaveDeletedChat()
      }
      if (readLastCopilotChatId() === id) {
        clearLastCopilotChatId()
      }
    } catch (deleteError) {
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : "Could not delete chat"
      )
    } finally {
      setDeleteBusy(false)
    }
  }

  const onPinChat = async (id: string, pinned: boolean) => {
    setMenuChatId(null)
    try {
      await userService.updateCopilotChat(id, { pinned })
      await refreshChats()
    } catch (pinError) {
      setError(
        pinError instanceof Error ? pinError.message : "Could not update pin"
      )
    }
  }

  const beginRename = (chat: SavedChat) => {
    setMenuChatId(null)
    setRenamingId(chat.id)
    setRenameDraft(chat.title.slice(0, CHAT_TITLE_MAX_LENGTH))
    setRenameBusy(false)
  }

  const commitRename = async () => {
    if (!renamingId || renameBusy) return
    const title = renameDraft.replace(/\s+/g, " ").trim().slice(0, CHAT_TITLE_MAX_LENGTH)
    if (!title) return
    const id = renamingId
    setRenameBusy(true)
    try {
      const response = await userService.updateCopilotChat(id, { title })
      const savedTitle = (response.data.data?.title || title).slice(
        0,
        CHAT_TITLE_MAX_LENGTH
      )
      setChats((current) =>
        current.map((chat) =>
          chat.id === id ? { ...chat, title: savedTitle } : chat
        )
      )
      cancelRename()
    } catch (renameError) {
      setRenameBusy(false)
      setError(
        renameError instanceof Error
          ? renameError.message
          : "Could not rename chat"
      )
    }
  }

  const persistMessages = async (
    id: string,
    messages: {
      role: "user" | "agent"
      text: string
      citations?: CopilotCitation[]
      attachments?: CopilotAttachmentMeta[]
    }[]
  ) => {
    if (!id || !messages.length) return
    await userService.appendCopilotMessages(id, messages)
    await refreshChats()
  }

  /** LAB-113: the answer as shown - readable source links, cited table rows - and the source of each [n]. */
  const citedAnswer = (answer: string, citations: CopilotCitation[]) => {
    const presented = presentImageAnswer(answer)
    const markers = positionedCitations(presented, citations)
    const text = citeTableRows(readableSourceLinks(presented), markers)
    return { markers, aligned: alignSharePointCitations(text, citations) }
  }

  const answerChecks = (
    line: ChatLine,
    index: number,
    cited: ReturnType<typeof citedAnswer>
  ) => {
    const previous = lines
      .slice(0, index)
      .reverse()
      .find((item) => item.role === "user")
    if (isCopilotCapacityMessage(cited.aligned.text || line.text)) {
      return { capacity: true, imageReview: null, pdfReview: null, chartReview: null, review: null }
    }
    // Reopened chats have no imageName, only the saved attachment metadata.
    const previousImageName =
      previous?.imageName || firstImageAttachment(previous?.attachments)?.name
    const answeredFromDocument = /embedded image|pdf text|document text|was not found in the embedded images/i.test(line.text)
    const imageReview = previousImageName && !answeredFromDocument
      ? reviewImageAnswer({ fileName: previousImageName, answer: line.text })
      : null
    const pdfFileName =
      previous?.documentName || previous?.pdfName || (previous ? pdfNameFromText(previous.text) : undefined)
    const pdfReview =
      pdfFileName && !/don'?t have access|do not have access|contact the owner/i.test(line.text)
        ? reviewPdfAnswer({
            fileName: pdfFileName,
            answer: line.text,
            extraction: previous?.documentText || previous?.pdfExtraction,
          })
        : null
    // LAB-107: each model the reply names is checked against its row in the chart reading.
    const chartReview =
      pdfFileName && previous
        ? reviewChartAnswer({
            fileName: pdfFileName,
            question: userMessageDisplayText(previous.text, previous.attachments || []),
            answer: line.text,
            extraction: previous.documentText || previous.pdfExtraction,
          })
        : null
    const review = reviewBesideProductPhotos(
      pdfReview || (imageReview && !(line.citations || []).length)
        ? null
        : reviewSharePointSources({
            question: previous?.text || "",
            answer: cited.aligned.text,
            citations: line.citations || [],
            scope: brandScope,
            markers: cited.markers,
          }),
      splitProductImages(line.text).images || ""
    )
    return { capacity: false, imageReview, pdfReview, chartReview, review }
  }

  const markerStatus = (marker: CopilotCitation, sources: ReviewedSource[]) => {
    const reviewed = sources.find((source) => sameDocumentUrl(source.url, marker.url))
    if (reviewed) return { verified: reviewed.status === "verified", reason: reviewed.reason }
    if (!marker.url || !/^https:\/\/[a-z0-9-]+\.sharepoint\.com\//i.test(marker.url)) {
      return { verified: false, reason: "Not an https SharePoint document link" }
    }
    if (!citationIsAllowed(marker, brandScope)) {
      return { verified: false, reason: "Outside your assigned brands" }
    }
    return { verified: true, reason: "Verified SharePoint source" }
  }

  /** LAB-113: the passage and page behind each SharePoint [n] of an answer. */
  const withCitationPassages = async (line: ChatLine): Promise<ChatLine> => {
    if (line.role !== "agent" || !line.citations?.length) return line
    const text = presentImageAnswer(splitProductImages(line.text).answer)
    const pending = markersNeedingPassages(text, line.citations).slice(0, 40)
    if (!pending.length) return { ...line, citations: citationsToSave(text, line.citations) }
    try {
      const results = await chatService.citationPassages(
        pending.map((citation) => ({
          url: citation.url as string,
          claims: claimsForMarker(text, citation.position as number),
        }))
      )
      const found = new Map<number, { excerpt?: string; page?: number }>()
      results.forEach((result, resultIndex) => {
        const position = pending[resultIndex]?.position
        if (position && result.found && result.excerpt) found.set(position, result)
      })
      return { ...line, citations: withPassages(text, line.citations, found) }
    } catch {
      return { ...line, citations: citationsToSave(text, line.citations) }
    }
  }

  const openCitationSource = (line: ChatLine, position: number) => {
    setOpenCitation({ lineId: line.id, position })
    // Answers saved before LAB-113 have markers but no passages; look them up once.
    const text = presentImageAnswer(splitProductImages(line.text).answer)
    const needed = markersNeedingPassages(text, line.citations || []).some(
      (citation) => citation.position === position
    )
    if (!needed || passageLookups.current.has(line.id)) return
    passageLookups.current.add(line.id)
    setPassageLoadingId(line.id)
    withCitationPassages(line)
      .then((updated) => {
        setLines((current) =>
          current.map((item) => (item.id === line.id ? { ...item, citations: updated.citations } : item))
        )
      })
      .finally(() => setPassageLoadingId(null))
  }

  const panelSource = ((): CitationPanelSource | null => {
    if (!openCitation) return null
    const line = lines.find((item) => item.id === openCitation.lineId)
    if (!line) return null
    const text = presentImageAnswer(splitProductImages(line.text).answer)
    const marker = positionedCitations(text, line.citations || []).find(
      (citation) => citation.position === openCitation.position
    )
    if (!marker) return null
    const status = markerStatus(marker, [])
    return {
      position: openCitation.position,
      citation: marker,
      verified: status.verified,
      reason: status.reason,
      claims: claimsForMarker(text, openCitation.position),
      loading: passageLoadingId === line.id,
    }
  })()

  // Scroll for a new message, not when an answer gains its citation passages.
  const lastLineId = lines[lines.length - 1]?.id
  React.useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight })
  }, [lastLineId, busy])

  React.useEffect(() => {
    if (!clientId || token) return undefined
    rememberCopilotLoginHint(loginHintRef.current)

    const params = new URLSearchParams(window.location.search)
    const authError = params.get("error")
    const authErrorDescription = params.get("error_description")
    const code = params.get("code")

    if (code) {
      window.history.replaceState({}, "", "/copilot")
      let cancelled = false
      setBusy(true)
      completeCopilotStudioSignIn(clientId, code)
        .then((accessToken) => {
          if (cancelled) return
          setNeedsManualSignIn(false)
          setToken(accessToken)
        })
        .catch((signInError: Error) => {
          if (cancelled) return
          setNeedsManualSignIn(true)
          setError(signInError.message)
        })
        .finally(() => {
          if (!cancelled) setBusy(false)
        })
      return () => {
        cancelled = true
      }
    }

    if (authError) {
      window.history.replaceState({}, "", "/copilot")
      if (
        copilotStudioAuthMode() === "silent" &&
        copilotSilentSignInNeedsInteraction(authError)
      ) {
        beginCopilotStudioSignIn(clientId, {
          silent: false,
          loginHint: loginHintRef.current,
        }).catch((signInError: Error) => {
          setNeedsManualSignIn(true)
          setError(signInError.message)
        })
        return undefined
      }
      setNeedsManualSignIn(true)
      setError(authErrorDescription || authError)
      return undefined
    }

    if (authStarted.current) return undefined
    authStarted.current = true
    let cancelled = false
    setBusy(true)
    refreshCopilotStudioToken(clientId)
      .then((accessToken) => {
        if (cancelled) return
        if (accessToken) {
          setToken(accessToken)
          setBusy(false)
          return
        }
        beginCopilotStudioSignIn(clientId, {
          silent: true,
          loginHint: loginHintRef.current,
        }).catch((signInError: Error) => {
          if (!cancelled) {
            setNeedsManualSignIn(true)
            setError(signInError.message)
            setBusy(false)
          }
        })
      })
      .catch((signInError: Error) => {
        if (!cancelled) {
          setNeedsManualSignIn(true)
          setError(signInError.message)
          setBusy(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [clientId, token])

  React.useEffect(() => {
    if (!token) {
      setBrandScope(null)
      setScopeReady(false)
      return undefined
    }
    let cancelled = false
    userService
      .getCopilotBrandScope()
      .then((response) => {
        if (cancelled) return
        setBrandScope(response.data.data)
        setScopeReady(true)
      })
      .catch(() => {
        if (cancelled) return
        setScopeReady(false)
        setError(
          "Could not load your brand access. Copilot stays closed so other brands are not shown."
        )
      })
    return () => {
      cancelled = true
    }
  }, [token])

  React.useEffect(() => {
    // Homepage landing: do not create a Copilot chat until the user sends a prompt.
    // On refresh / relaunch, reopen the last chat they were viewing.
    if (!token || !scopeReady || started.current) return undefined
    started.current = true
    let cancelled = false
    setBusy(true)
    setError("")
    ;(async () => {
      try {
        await refreshChats()
        if (cancelled) return
        const lastId = readLastCopilotChatId()
        if (!lastId) return
        const response = await userService.getCopilotChat(lastId)
        if (cancelled) return
        const chat = response.data.data
        chatIdRef.current = chat.id
        setChatId(chat.id)
        setConversationId(chat.copilotConversationId || "")
        rememberLastCopilotChatId(chat.id)
        setLines(
          (chat.messages || []).map((message) => ({
            id: message.id,
            role: message.role,
            text: message.text,
            citations: message.citations,
            attachments:
              message.role === "user"
                ? message.attachments?.length
                  ? message.attachments
                  : attachmentsFromText(message.text)
                : [],
          }))
        )
      } catch {
        clearLastCopilotChatId()
        // Empty welcome stays; first prompt will create a new chat.
      } finally {
        if (!cancelled) setBusy(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token, scopeReady, session])

  const onSignIn = () => {
    setError("")
    setNeedsManualSignIn(false)
    beginCopilotStudioSignIn(clientId, {
      silent: false,
      loginHint,
    }).catch((signInError: Error) => {
      setNeedsManualSignIn(true)
      setError(signInError.message)
    })
  }

  const onSignOut = () => {
    clearCopilotStudioToken()
    clearLastCopilotChatId()
    setToken(null)
    setConversationId("")
    setChatId("")
    chatIdRef.current = ""
    setLines([])
    started.current = false
  }

  const startNewChat = () => {
    clearLastCopilotChatId()
    started.current = false
    chatIdRef.current = ""
    setChatId("")
    setConversationId("")
    setLines([])
    setError("")
    setSession((value) => value + 1)
  }

  const openChat = async (id: string) => {
    setBusy(true)
    setError("")
    try {
      const response = await userService.getCopilotChat(id)
      const chat = response.data.data
      started.current = true
      chatIdRef.current = chat.id
      setChatId(chat.id)
      setConversationId(chat.copilotConversationId || "")
      rememberLastCopilotChatId(chat.id)
      setLines(
        (chat.messages || []).map((message) => ({
          id: message.id,
          role: message.role,
          text: message.text,
          citations: message.citations,
          // LAB-103: saved metadata, or the "Attached <name>" line of an older chat.
          attachments:
            message.role === "user"
              ? message.attachments?.length
                ? message.attachments
                : attachmentsFromText(message.text)
              : [],
        }))
      )
    } catch {
      setError("Could not open that chat.")
    } finally {
      setBusy(false)
    }
  }

  const ensureConversation = async () => {
    if (conversationId) return conversationId
    setStatus("Starting chat…")
    const result = await startCopilotConversation(connection)
    const nextId = result.conversationId || ""
    if (!nextId) {
      throw new Error(
        "The agent conversation did not start. Confirm LabWizard Standard Agent is published, then sign out and sign in again."
      )
    }
    setConversationId(nextId)
    const created = await userService.createCopilotChat(nextId)
    const { id } = created.data.data
    chatIdRef.current = id
    setChatId(id)
    rememberLastCopilotChatId(id)
    await refreshChats()
    return nextId
  }

  const clearFile = () => {
    setFile(null)
    // Reset the native input too, or picking the same file again fires no change event.
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const onSend = async () => {
    const text = draft.trim()
    if ((!text && !file) || !token || busy) return
    setBusy(true)
    setError("")
    let activeConversationId = conversationId
    try {
      activeConversationId = await ensureConversation()
    } catch (startError) {
      setBusy(false)
      setStatus("")
      setError(startError instanceof Error ? startError.message : "Could not start chat")
      return
    }
    const imageName = file && isImageAttachment(file) ? file.name : undefined
    const pdfName = file && isPdfAttachment(file) ? file.name : undefined
    let attachment:
      | { name: string; contentType: string; contentUrl: string }
      | undefined
    try {
      if (file && imageName) {
        attachment = {
          name: file.name,
          contentType: attachmentContentType(file),
          contentUrl: await readFileAsDataUrl(file),
        }
      }
    } catch (readError) {
      setBusy(false)
      setError(readError instanceof Error ? readError.message : "Could not read the file")
      return
    }
    const priorDocument = [...lines]
      .reverse()
      .find((line) => line.role === "user" && (line.documentText || line.pdfExtraction))
    let documentText = ""
    if (file) {
      setStatus("Reading the document…")
      try {
        // LAB-107: a which-models-have-features question reads a chart row by row.
        const described = await chatService.readImage(
          file,
          withChartReadingGuidance(text || "Read this document.")
        )
        documentText = (described.text || "").trim()
      } catch (readError) {
        setError(readError instanceof Error ? readError.message : "Could not read the document")
      } finally {
        setStatus("")
      }
    } else {
      documentText = priorDocument?.documentText || priorDocument?.pdfExtraction || ""
    }
    const documentName =
      file?.name || priorDocument?.documentName || priorDocument?.pdfName || (priorDocument ? pdfNameFromText(priorDocument.text) : "") || ""
    const userText = text || (file ? `Attached ${file.name}` : "")
    // LAB-103: the card stays on the message it was sent with, for every file type.
    const sentAttachments = file ? [attachmentMetaFromFile(file)] : []
    setLines((current) => [
      ...current,
      {
        id: `user-${Date.now()}`,
        role: "user",
        text: userText,
        imageUrl: imageName ? attachment?.contentUrl : undefined,
        imageName,
        pdfName,
        pdfExtraction: pdfName ? documentText : undefined,
        documentName: documentName || undefined,
        documentText: documentText || undefined,
        attachments: sentAttachments,
      },
    ])
    setDraft("")
    clearFile()
    const activeName = documentName
    const activeExtraction = documentText
    // LAB-109: look up product images while the agent answers. A failed lookup adds nothing.
    const productImageRequest = !file && !activeExtraction && isProductImageRequest(text)
    const productImages: Promise<CopilotProductImageResult | null> = productImageRequest
      ? userService
          .getCopilotProductImages(text)
          .then((response) => response.data.data)
          .catch(() => null)
      : Promise.resolve(null)
    try {
      let visible: ChatLine[] = []
      const documentQuestion = text || (file ? "What does this document say? Quote the values it states." : "")
      if (file && !activeExtraction) {
        setError("Could not read the document")
      } else if (activeName && activeExtraction && documentQuestion) {
        setStatus("Answering from the document…")
        // LAB-107: list a model only when its chart row shows every feature asked for.
        const answer = await chatService.answerDocument({
          fileName: activeName,
          question: withChartLookupGuidance(documentQuestion),
          extraction: activeExtraction,
        })
        visible = [
          {
            id: `agent-doc-${Date.now()}`,
            role: "agent",
            text: answer,
          },
        ]
      } else {
      const result = await sendCopilotMessage({
        ...connection,
        conversationId: activeConversationId,
        text: scopeCopilotQuestion(text, brandScopeRef.current, {
          imageAttached: Boolean(imageName),
          documentAttached: false,
          productImageRequest,
        }),
        value: copilotBrandActivityValue(brandScopeRef.current),
        attachment,
      })
      if (result.conversationId) setConversationId(result.conversationId)
      visible = visibleReplies(result.activities)
      const lastReply = visible[visible.length - 1]
      if (
        text &&
        (!lastReply || !citesSharePointDocument(lastReply.text, lastReply.citations || []))
      ) {
        setStatus("Checking SharePoint documents…")
        try {
          const grounded = await chatService.sharePointSources(text)
          if (grounded.trim()) {
            const citations = citationsFromText(grounded)
            const replacement = {
              id: lastReply?.id || `agent-sources-${Date.now()}`,
              role: "agent" as const,
              text: grounded,
              citations,
            }
            visible = lastReply ? [...visible.slice(0, -1), replacement] : [replacement]
          }
        } catch {
          // The agent reply stays when SharePoint search is unavailable.
        }
      }
      // LAB-104: a recommendation compares the pick with a named alternative model.
      // The agent does not always add it, so ask once, in the same conversation.
      const answered = visible[visible.length - 1]
      if (
        answered &&
        answered.text !== BRAND_ACCESS_DENIED &&
        !isCopilotCapacityMessage(answered.text) &&
        needsComparisonFollowUp(text, answered.text)
      ) {
        setStatus("Adding a model comparison…")
        try {
          const followUp = await sendCopilotMessage({
            ...connection,
            conversationId: result.conversationId || activeConversationId,
            text: scopeCopilotQuestion(COMPARISON_FOLLOW_UP, brandScopeRef.current, {
              answerGuides: false,
            }),
            value: copilotBrandActivityValue(brandScopeRef.current),
          })
          const added = visibleReplies(followUp.activities).filter(
            (reply) =>
              reply.text.trim() &&
              reply.text !== BRAND_ACCESS_DENIED &&
              !isCopilotCapacityMessage(reply.text)
          )
          if (added.length) visible = [...visible, ...added]
        } catch {
          // The first answer stays when the follow-up fails.
        }
      }
      }
      const agentReplied = visible.length > 0
      const found = await productImages
      const last = visible[visible.length - 1]
      // Images are already limited to the user's brands; a withheld answer still gets none.
      if (found && last?.text !== BRAND_ACCESS_DENIED && !isCopilotCapacityMessage(last?.text || "")) {
        visible = last
          ? [...visible.slice(0, -1), { ...last, text: withProductImages(last.text, found) }]
          : [{ id: `agent-images-${Date.now()}`, role: "agent", text: withProductImages("", found) }]
      }
      if (visible.length) setLines((current) => [...current, ...visible])
      // LAB-113: find each citation's passage and page before saving, so a reopened chat shows them too.
      if (visible.some((line) => markersNeedingPassages(presentImageAnswer(splitProductImages(line.text).answer), line.citations || []).length)) {
        setStatus("Linking citations to document passages…")
        const shown = visible
        const enriched = await Promise.race([
          Promise.all(shown.map(withCitationPassages)),
          new Promise<ChatLine[]>((resolve) => {
            setTimeout(() => resolve(shown), CITATION_PASSAGE_TIMEOUT_MS)
          }),
        ])
        if (enriched !== shown) {
          setLines((current) => current.map((line) => enriched.find((item) => item.id === line.id) || line))
          visible = enriched
        }
      }
      if (!agentReplied && !(file && !activeExtraction)) {
        setError(
          "The agent returned no reply. Confirm LabWizard Standard Agent is published, then sign out and sign in again."
        )
      }
      const id = chatIdRef.current
      if (id) {
        try {
          await persistMessages(id, [
            {
              role: "user",
              text: file ? [text, `Attached ${file.name}`].filter(Boolean).join("\n") : text,
              attachments: sentAttachments,
            },
            ...visible.map((line) => ({
              role: "agent" as const,
              text: line.text,
              citations: line.citations,
            })),
          ])
        } catch {
          setError("This reply was not saved to your chat history.")
        }
      }
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : "Send failed")
    } finally {
      setStatus("")
      setBusy(false)
    }
  }

  if (!clientId) {
    return (
      <div className={styles.empty}>
        <Text variant="h3" as="h1">
          Microsoft sign-in is not configured
        </Text>
        <Text variant="body2" color="text70Color">
          Set REACT_APP_COPILOT_STUDIO_CLIENT_ID to an Entra app registration
          with delegated CopilotStudio.Copilots.Invoke, and add this redirect
          URI: {window.location.origin}/copilot
        </Text>
      </div>
    )
  }

  if (!token) {
    return (
      <div className={styles.empty}>
        <Text variant="h3" as="h1">
          LabWizard Standard Agent
        </Text>
        <Text variant="body2" color="text70Color">
          {needsManualSignIn
            ? "Microsoft could not reuse your portal sign-in. Continue once so SharePoint answers use your own access."
            : "Using your portal sign-in for SharePoint access."}
        </Text>
        {error ? (
          <Text variant="body2" color="redColor">
            {error}
          </Text>
        ) : null}
        {needsManualSignIn ? (
          <Button color="primary" disabled={busy} onClick={onSignIn}>
            Continue with Microsoft
          </Button>
        ) : (
          <Text variant="body2">Signing you in…</Text>
        )}
      </div>
    )
  }

  const statusHints = (
    <>
      {file ? (
        <Text variant="caption1" color="text70Color" className={styles.hint}>
          The answer uses this document only. Each value names the page and whether it came from an embedded image or from the document text.
          A question that asks which models have given features is checked row by row against the chart.
        </Text>
      ) : null}
      {error ? (
        <Text variant="caption1" color="redColor" className={styles.error}>
          {error}
        </Text>
      ) : null}
    </>
  )

  const composer = (
    <form
      className={styles.composer}
      onSubmit={(event) => {
        event.preventDefault()
        onSend()
      }}
    >
      {file ? (
        <CopilotAttachmentCard
          attachment={attachmentMetaFromFile(file)}
          onRemove={clearFile}
          disabled={busy}
        />
      ) : null}
      <textarea
        ref={promptInputRef}
        className={styles.input}
        rows={1}
        value={draft}
        placeholder="Ask LabWizard Standard Agent"
        aria-label="Prompt"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) {
            return
          }
          event.preventDefault()
          onSend()
        }}
      />
      <div className={styles.composerActions}>
        {/* file input is the control; visually hidden beside this label */}
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className={styles.file} htmlFor="copilot-file">
          <Icon name="paperclip" />
          Attach
        </label>
        <input
          id="copilot-file"
          ref={fileInputRef}
          className={styles.fileInput}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.ppt,.pptx,.txt,.csv"
          onChange={(event) => {
            const next = event.currentTarget.files?.[0]
            if (next && next.size > MAX_ATTACHMENT_BYTES) {
              setError("File must be 15 MB or smaller.")
              clearFile()
              return
            }
            setFile(next || null)
          }}
        />
        <Button color="primary" size="medium" type="submit" disabled={busy}>
          Send
        </Button>
      </div>
    </form>
  )

  const renderHistoryItem = (chat: SavedChat) => {
    const active = chat.id === chatId
    const menuOpen = menuChatId === chat.id
    return (
      <div
        key={chat.id}
        className={[
          styles.historyItem,
          active ? styles.historyItemActive : "",
          menuOpen ? styles.historyItemMenuOpen : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <button
          type="button"
          className={styles.historyOpen}
          onClick={() => openChat(chat.id)}
        >
          {chat.pinned ? (
            <Icon name="pushpin" className={styles.historyPin} />
          ) : null}
          <span className={styles.historyTitle}>{chat.title}</span>
        </button>
        <div className={styles.historyActions}>
          <button
            type="button"
            className={styles.historyMore}
            aria-label="Chat actions"
            aria-expanded={menuOpen}
            onClick={(event) => {
              event.stopPropagation()
              setMenuChatId(menuOpen ? null : chat.id)
            }}
          >
            <Icon name="dots" />
          </button>
          {menuOpen ? (
            <div className={styles.historyMenu} role="menu">
              <button
                type="button"
                role="menuitem"
                className={styles.historyMenuItem}
                onClick={() => onPinChat(chat.id, !chat.pinned)}
              >
                <Icon name={chat.pinned ? "unpin" : "pushpin"} />
                {chat.pinned ? "Unpin" : "Pin"}
              </button>
              <button
                type="button"
                role="menuitem"
                className={styles.historyMenuItem}
                onClick={() => beginRename(chat)}
              >
                <Icon name="edit" />
                Rename
              </button>
              <button
                type="button"
                role="menuitem"
                className={`${styles.historyMenuItem} ${styles.historyMenuDanger}`}
                onClick={() => beginDeleteChat(chat)}
              >
                <Icon name="trash-can" />
                Delete
              </button>
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <aside className={styles.history}>
        <div className={styles.historyHead}>
          <Text variant="body2">LabWizard</Text>
          <Button variant="outlined" size="small" color="primary" onClick={startNewChat}>
            New
          </Button>
        </div>
        <div className={styles.historyList} ref={historyMenuRef}>
          {pinnedChats.length ? (
            <div className={styles.historySection}>
              <Text variant="caption1" color="text70Color" className={styles.historySectionLabel}>
                Pinned
              </Text>
              {pinnedChats.map(renderHistoryItem)}
            </div>
          ) : null}
          <div className={styles.historySection}>
            <Text variant="caption1" color="text70Color" className={styles.historySectionLabel}>
              Recent chats
            </Text>
            {recentChats.length ? (
              recentChats.map(renderHistoryItem)
            ) : (
              <Text variant="caption1" color="text70Color">
                {pinnedChats.length
                  ? "Unpinned chats appear here."
                  : "Your chats appear here."}
              </Text>
            )}
          </div>
        </div>
      </aside>
    <div className={styles.chat}>
      <div className={styles.toolbar}>
        <Text variant="body2">LabWizard Standard Agent · your SharePoint access</Text>
        <Button variant="outlined" size="small" color="primary" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
      {!hasUserQuery ? (
        <div className={styles.landing}>
          {!scopeReady && !error ? (
            <div className={styles.agent}>Checking your brand access…</div>
          ) : null}
          {scopeReady && !busy ? (
            <div className={styles.welcome}>
              <Text variant="h2" as="h1" weight="bold">
                Hello, how can I help you today?
              </Text>
              <Text variant="body1" color="text70Color">
                Ask a question, get insights, or work on your sales tasks with LabWizard.
              </Text>
            </div>
          ) : null}
          {statusHints}
          {composer}
        </div>
      ) : (
        <>
      <div className={styles.messages} ref={listRef}>
        {lines.map((line, index) => {
          // LAB-109: the product image block is LabWizard's, so source checks skip it.
          const parts = line.role === "agent" ? splitProductImages(line.text) : null
          const cited = parts ? citedAnswer(parts.answer, line.citations || []) : null
          const checks = line.role === "agent" && cited ? answerChecks(line, index, cited) : null
          const review = checks?.review || null
          const markers = cited?.markers || []
          return (
          <div
            key={line.id}
            className={
              line.role === "user"
                ? line.imageUrl
                  ? `${styles.user} ${styles.userWithImage}`
                  : styles.user
                : styles.agent
            }
          >
            {line.imageUrl ? (
              <img className={styles.preview} src={line.imageUrl} alt={line.imageName || "Uploaded"} />
            ) : null}
            {line.attachments?.length ? (
              <div className={styles.attachments}>
                {line.attachments.map((item, attachmentIndex) => (
                  <CopilotAttachmentCard
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${item.name}-${attachmentIndex}`}
                    attachment={item}
                  />
                ))}
              </div>
            ) : null}
            {line.role === "agent" ? (
              <CopilotMessageBody
                text={[review?.annotated || cited?.aligned.text, parts?.images].filter(Boolean).join("\n\n")}
                citations={{
                  open: (position) => openCitationSource(line, position),
                  label: (position) => {
                    const marker = markers.find((item) => item.position === position)
                    return marker ? citationLabel(marker) : undefined
                  },
                }}
              />
            ) : (
              userMessageDisplayText(line.text, line.attachments || [])
            )}
            {line.role === "agent" && checks
              ? (() => {
                  if (checks.capacity) {
                    return (
                      <div className={styles.sources}>
                        <div className={styles.unverified}>
                          Copilot Studio capacity for this environment is exhausted. A Power Platform admin must add
                          Copilot Credits or confirm pay-as-you-go billing for the agent environment, then wait a few
                          minutes and try again.
                        </div>
                      </div>
                    )
                  }
                  const { imageReview, pdfReview, chartReview } = checks
                  if (!imageReview && !pdfReview && !chartReview && !review) return null
                  const linkedOnly = review
                    ? review.sources.filter(
                        (source) => !markers.some((marker) => sameDocumentUrl(marker.url, source.url))
                      )
                    : []
                  return (
                    <div className={styles.sources}>
                      {imageReview ? (
                        <div className={imageReview.grounded ? styles.verified : styles.unverified}>
                          {imageReview.summary}
                        </div>
                      ) : null}
                      {pdfReview ? (
                        <>
                          <div className={pdfReview.grounded ? styles.verified : styles.unverified}>
                            {pdfReview.summary}
                          </div>
                          {pdfReview.citations.map((citation) => (
                            <div key={`${citation.page}-${citation.source}`}>
                              <span>{citation.title}</span>
                              <div className={styles.verified}>{citation.reason}</div>
                            </div>
                          ))}
                        </>
                      ) : null}
                      {chartReview ? (
                        <>
                          <div className={chartReview.grounded ? styles.verified : styles.unverified}>
                            {chartReview.summary}
                          </div>
                          {chartReview.items.map((item) => (
                            <div key={item.item}>
                              <span>{item.item}</span>
                              <div
                                className={
                                  item.status === "verified"
                                    ? styles.verified
                                    : styles.unverified
                                }
                              >
                                {item.reason}
                              </div>
                            </div>
                          ))}
                        </>
                      ) : null}
                      {review ? (
                        <>
                          {markers.length || linkedOnly.length ? (
                            <div className={styles.referenceBlock}>
                              <div className={styles.referenceHeading}>Sources</div>
                              {markers.length ? (
                                <ol className={`${styles.referenceList} ${styles.numberedReferences}`}>
                                  {markers.map((marker) => {
                                    const status = markerStatus(marker, review.sources)
                                    return (
                                      <li key={marker.position} value={marker.position}>
                                        <span className={styles.referenceNumber} aria-hidden="true">
                                          {marker.position}
                                        </span>
                                        <button
                                          type="button"
                                          className={styles.referenceButton}
                                          onClick={() => openCitationSource(line, marker.position as number)}
                                        >
                                          {citationLabel(marker)}
                                          {marker.page ? `, page ${marker.page}` : ""}
                                        </button>
                                        <span
                                          className={status.verified ? styles.refStatusOk : styles.refStatusWarn}
                                          title={status.reason}
                                        >
                                          {status.verified ? "verified" : "unverified"}
                                        </span>
                                      </li>
                                    )
                                  })}
                                </ol>
                              ) : null}
                              {linkedOnly.length ? (
                                <ul className={styles.referenceList}>
                                  {linkedOnly.map((source) => (
                                    <li key={source.url || source.title}>
                                      {source.url ? (
                                        <a href={source.url} target="_blank" rel="noreferrer">
                                          {citationLabel(source)}
                                        </a>
                                      ) : (
                                        <span>{citationLabel(source)}</span>
                                      )}
                                      <span
                                        className={
                                          source.status === "verified" ? styles.refStatusOk : styles.refStatusWarn
                                        }
                                        title={source.reason}
                                      >
                                        {source.status === "verified" ? "verified" : "unverified"}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              ) : null}
                            </div>
                          ) : null}
                          <div className={review.grounded ? styles.verified : styles.unverified}>
                            {review.summary}
                          </div>
                          <details className={styles.sourceTip}>
                            <summary>How sources and citations work</summary>
                            <p>{review.rulesTip || SHAREPOINT_RETRIEVAL_SUCCESS}</p>
                            <p>
                              Select a numbered citation to see the file, the passage that supports the claim and its
                              page. Statements with no supporting document are labelled &ldquo;Not tied to a cited
                              document&rdquo;.
                            </p>
                          </details>
                        </>
                      ) : null}
                    </div>
                  )
                })()
              : null}
          </div>
          )
        })}
        {busy ? <div className={styles.agent}>{status || "Working…"}</div> : null}
      </div>
      {statusHints}
      <div className={styles.composerDock}>{composer}</div>
      {panelSource ? (
        <CopilotCitationPanel source={panelSource} onClose={() => setOpenCitation(null)} />
      ) : null}
        </>
      )}
    </div>
    {deletingChat ? (
      <div
        className={styles.renameOverlay}
        role="presentation"
        onClick={(event) => {
          // Only a click on the backdrop itself closes the dialog.
          if (event.target === event.currentTarget) cancelDeleteChat()
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") cancelDeleteChat()
        }}
      >
        <div
          className={styles.renameDialog}
          role="dialog"
          aria-modal="true"
          aria-labelledby="copilot-delete-title"
          aria-describedby="copilot-delete-body"
        >
          <Text id="copilot-delete-title" variant="h3" as="h2" weight="bold">
            Delete chat?
          </Text>
          <Text id="copilot-delete-body" variant="body2" color="text70Color">
            This will permanently delete <strong>{deletingChat.title}</strong>.
            This can&apos;t be undone.
          </Text>
          <div className={styles.renameActions}>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              type="button"
              disabled={deleteBusy}
              onClick={cancelDeleteChat}
            >
              Cancel
            </Button>
            <Button
              className={styles.deleteConfirmButton}
              color="red"
              size="medium"
              type="button"
              disabled={deleteBusy}
              onClick={() => onDeleteChat(deletingChat.id)}
            >
              {deleteBusy ? "Deleting…" : "Delete chat"}
            </Button>
          </div>
        </div>
      </div>
    ) : null}
    {renamingId ? (
      <div
        className={styles.renameOverlay}
        role="presentation"
        onClick={(event) => {
          // Only a click on the backdrop itself closes the dialog.
          if (event.target === event.currentTarget) cancelRename()
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") cancelRename()
        }}
      >
        <div
          className={styles.renameDialog}
          role="dialog"
          aria-modal="true"
          aria-labelledby="copilot-rename-title"
        >
          <Text id="copilot-rename-title" variant="h3" as="h2" weight="bold">
            Rename chat
          </Text>
          <Text variant="caption1" color="text70Color">
            Enter a new name for this chat. Maximum {CHAT_TITLE_MAX_LENGTH} characters.
          </Text>
          <input
            className={styles.renameInput}
            value={renameDraft}
            maxLength={CHAT_TITLE_MAX_LENGTH}
            aria-label="Chat name"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            disabled={renameBusy}
            onChange={(event) =>
              setRenameDraft(event.target.value.slice(0, CHAT_TITLE_MAX_LENGTH))
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault()
                commitRename()
              }
              if (event.key === "Escape") {
                event.preventDefault()
                cancelRename()
              }
            }}
          />
          <div className={styles.renameMeta}>
            <Text variant="caption1" color="text70Color">
              {renameDraft.length}/{CHAT_TITLE_MAX_LENGTH}
            </Text>
          </div>
          <div className={styles.renameActions}>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              type="button"
              disabled={renameBusy}
              onClick={cancelRename}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              size="medium"
              type="button"
              disabled={renameBusy || !renameDraft.replace(/\s+/g, " ").trim()}
              onClick={commitRename}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    ) : null}
    </div>
  )
})

export default CopilotStudioSdkChat
